import { PencilSquareIcon } from '@heroicons/react/24/solid';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { UserActivity } from '~/schemas/userActivity';
import { ReactComponent as Whatsapp } from '~/assets/svg/whatsapp.svg';
import { ReactComponent as Instagram } from '~/assets/svg/instagram.svg';
import { MessagingProducts } from '~/types';
import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_COMMENT } from '~/services/comment';
import ActivityDialog from './ActivityDialog';
import { ActivityRecord } from '~/store/activityRecords';
import { motion } from 'motion/react';

export const ActivityCardComent = ({
  activity,
}: {
  activity: UserActivity;
}) => {
  const [t] = useTranslation('activity');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [comment, setComment] = useState(activity.comment || '');
  const [typeOfComment, setTypeOfComment] = useState(
    activity.typeOfComment || '',
  );
  const IconComponent = {
    [MessagingProducts.WHATSAPP]: Whatsapp,
    [MessagingProducts.INSTAGRAM]: Instagram,
    [MessagingProducts.EMAIL]: null, // Add other messaging products as needed
    [MessagingProducts.SMS]: null, // Add SMS entry
  };

  const handleIconClick = () => {
    setIsDialogOpen(true);
  };

  const handleCommentUpdate = (updatedComment: any) => {
    setComment(updatedComment.data.update_comment.data.comment);
    setTypeOfComment(updatedComment.data.update_comment.data.typeOfComment);
  };

  const SelectedIcon =
    IconComponent[activity.messaging_product as MessagingProducts] ?? Whatsapp;

  return (
    <motion.div
      className="flex gap-4 border-l pt-4"
      whileHover={{
        x: 4,
        transition: { duration: 0.2 },
      }}
    >
      {SelectedIcon && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
          animate={{
            opacity: 1,
            scale: 1,
            rotateY: 0,
            transition: {
              delay: 0.2,
              type: 'spring',
              stiffness: 300,
              damping: 20,
            },
          }}
          whileHover={{
            scale: 1.1,
            rotateY: 5,
            transition: { duration: 0.2 },
          }}
        >
          <SelectedIcon className="ml-[-12px] mt-2 h-6 w-6 text-current" />
        </motion.div>
      )}

      <motion.div
        className="w-full rounded-lg border border-blue-400 bg-blue-100 p-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: {
            delay: 0.1,
            type: 'spring',
            stiffness: 300,
            damping: 25,
          },
        }}
        whileHover={{
          y: -2,
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
          borderColor: 'rgba(59, 130, 246, 0.6)',
          backgroundColor: 'rgba(219, 234, 254, 1)',
          transition: { duration: 0.2 },
        }}
      >
        <div className="flex flex-row items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{
              opacity: 1,
              x: 0,
              transition: {
                delay: 0.2,
                type: 'spring',
                stiffness: 300,
                damping: 20,
              },
            }}
          >
            <motion.h3 className="text-md font-bold" whileHover={{ x: 2 }}>
              {t('title_Observations')}
            </motion.h3>
            <div className="flex flex-row gap-2 text-sm">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { delay: 0.4 },
                }}
              >
                {moment(activity.created_at).format('lll')}
              </motion.p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                delay: 0.3,
                type: 'spring',
                stiffness: 400,
                damping: 20,
              },
            }}
            whileHover={{
              scale: 1.2,
              rotate: 5,
              transition: { duration: 0.2 },
            }}
            whileTap={{
              scale: 0.9,
              rotate: -5,
              transition: { duration: 0.1 },
            }}
          >
            <PencilSquareIcon
              className="h-5 w-5 cursor-pointer fill-current"
              onClick={handleIconClick}
            />
          </motion.div>
        </div>

        <motion.p
          className="mt-[10px]"
          initial={{ opacity: 0, y: 10 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              delay: 0.5,
              type: 'spring',
              stiffness: 300,
              damping: 25,
            },
          }}
        >
          <motion.strong
            whileHover={{ scale: 1.05 }}
            style={{ display: 'inline-block' }}
          >
            [{t(typeOfComment)}]:
          </motion.strong>{' '}
          {comment}
        </motion.p>
      </motion.div>

      {isDialogOpen && (
        <ActivityDialog
          onClose={() => setIsDialogOpen(false)}
          open={isDialogOpen}
          activity={activity}
          type="edit"
          onCommentUpdate={handleCommentUpdate}
        />
      )}
    </motion.div>
  );
};
